import { useMemo } from 'react'

import {
  digObject, matchFilterArrayIncludes, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProviderModel, ProviderRequestOptions } from '@models/types'

const watchEntityKeys = ['providers']

export const sortPreferredProviders = (providers: ProviderModel[]) => providers.sort((a, b) => {
  const sortAPreferred = digObject(a, 'options.preferred_supplier', false)
  const sortBPreferred = digObject(b, 'options.preferred_supplier', false)

  const sortATitle = a.title
  const sortBTitle = b.title

  if (sortAPreferred > sortBPreferred) return -1
  if (sortAPreferred < sortBPreferred) return 1
  if (sortATitle > sortBTitle) return 1
  if (sortATitle < sortBTitle) return -1
  return 0
})

type ProviderFilters = {
  active?: string,
  limit?: number,
  service_id?: number,
  service_product_id?: number,
  service_type?: string,
  string?: string,
}

type UseProvidersOptions = {
  filters?: ProviderFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderRequestOptions,
}

function useProviders(options: UseProvidersOptions) {
  const { filters = {}, requestOptions } = options
  const {
    active: filterActive,
    limit: filterLimit,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    service_type: filterServiceType,
    string,
  } = filters

  const {
    updatedEntities: { providers: providersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providers } = useSelector(reduxState => reduxState.entities)

  const filteredProviders = useMemo(() => {
    const filtered = Object.values(providers).filter((provider: ProviderModel) => {
      const {
        active, id, service_ids = [], service_product_ids = [], service_types = [], title,
      } = provider

      const matchActive = filterActive ? active.toString() === filterActive : true
      const matchService = matchFilterArrayIncludes(service_ids, Number(filterServiceId))
      const matchServiceProduct = matchFilterArrayIncludes(service_product_ids, filterServiceProductId)
      const matchServiceType = matchFilterArrayIncludes(service_types, filterServiceType)

      const matchId = id.toString() === string
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchActive
        && matchService
        && matchServiceProduct
        && matchServiceType
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providersUpdatedAt, JSON.stringify(filters)])

  const filteredProvidersCount = filteredProviders.length
  const hasFilteredProviders = !!filteredProvidersCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredProvidersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviders } = useReduxAction(
    'providers',
    'loadProviders',
    {
      include_provider_product_code_product_ids: !!filterServiceProductId,
      include_provider_service_ids: !!filterServiceId,
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    // @ts-expect-error TS2339
    { shouldPerformFn: ({ loading }) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviders,
    filteredProvidersCount,
    hasFilteredProviders,
    loading: loadingProviders,
  }
}

export default useProviders
